import { useCallback, useEffect, useState } from 'react'
import {
  createSearchParams,
  URLSearchParamsInit,
  useLocation,
  useNavigate as useNavigate_,
  useParams,
} from 'react-router'
import {
  useInfiniteQuery,
  useQuery,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from '@tanstack/react-query'
import { isUndefined, omitBy } from 'lodash'

import { getDistanceUnits } from '@/common/utils/formatting'
import { RegionResponse } from '@/modules/api/openapi/models/RegionResponse'
import { queryClientInstance } from '@/modules/api/queryClient'
import { apiV2, internalApi } from '@/modules/api/request'
import { OpenApiQueryParamsOmitRegionId } from '@/modules/api/types'
import { getNextPageParam } from '@/modules/api/util'
import { RegionResponseExternal } from '@/modules/api/v2/models/RegionResponseExternal'
import { SEGMENTS } from '@/modules/urlRouting/paths'
import { LocationState, NavigateOptionsWithParams } from '@/modules/urlRouting/types'
import { generatePathWithRegionId } from '@/modules/urlRouting/utils'

const REGIONS_PER_PAGE = 10

type CurrentProducts = 'mobility' | 'management' | 'regulations' | 'elevate' | 'public' | undefined

export const useCurrentProduct: () => CurrentProducts = () => {
  const { pathname } = useLocation()
  return [
    SEGMENTS.PUBLIC,
    SEGMENTS.MOBILITY,
    SEGMENTS.MANAGEMENT,
    SEGMENTS.REGULATIONS,
    SEGMENTS.ELEVATE,
  ].find(path => pathname.includes(`${path}/`)) as CurrentProducts
}

export const useRegions = (
  queryParams: OpenApiQueryParamsOmitRegionId<typeof internalApi.regions.getRegions> = {}
) => {
  return useInfiniteQuery({
    queryKey: [`/regions`, queryParams],
    queryFn: async ({ pageParam }) => {
      const regionsResponse = await internalApi.regions.getRegions({
        page: pageParam,
        size: REGIONS_PER_PAGE,
        ...queryParams,
      })

      regionsResponse.items.forEach(region =>
        queryClientInstance.setQueryData<RegionResponse>([`/regions/${region.regionId}`], region)
      )

      return regionsResponse
    },
    initialPageParam: 1,
    getNextPageParam,
  })
}

/**
 * Gets a region object if the user has access to it
 *
 * Take care observing this from anything the gatekeeper renders *because* this query
 * failed. `RegionGatekeeper` unmounts its children whenever the region goes back to
 * pending, and re-observing an errored query refetches it, which resets a query that
 * never held data to pending - so the two livelock and the page spins forever. See
 * `NavigateToDefaultRegion`, which is careful never to look up the id in the path.
 */
export const useRegion = (regionId?: string) => {
  return useQuery({
    queryKey: [`/regions/${regionId}`],
    queryFn: async () => await internalApi.regions.getRegionById({ regionId: regionId! }),
    enabled: !!regionId,
  })
}

/**
 * @returns Region associated with /:regionId param only if that user has access.
 * Throws an error if the user does not have access.
 * To force RegionReponse as the typed response, use this line:
 * useCurrentRegion<RegionResponse>()
 */
export const useCurrentRegion = <T = RegionResponse | RegionResponseExternal>() => {
  const { regionId } = useParams()
  const productPath = useCurrentProduct()

  if (!regionId) throw Error('Error retreiving regionId from url parameters')

  const externalQueryParams = {
    queryKey: [`/v2/regions/${regionId}`],
    queryFn: async () => await apiV2.regions.getRegionById({ regionId }),
  }

  const internalQueryParams = {
    queryKey: [`/regions/${regionId}`],
    queryFn: async () => await internalApi.regions.getRegionById({ regionId }),
  }

  return useSuspenseQuery(
    productPath === SEGMENTS.PUBLIC ? externalQueryParams : internalQueryParams
  ) as UseSuspenseQueryResult<T>
}

/**
 * @returns Translated distance-unit abbreviation ('km' / 'mi') for the current region.
 */
export const useDistanceUnits = () => {
  const { data } = useCurrentRegion()
  return getDistanceUnits(data.isMetric)
}

/**
 * @returns location and unpacked location.state
 */
export const useLocationState = () => {
  const location = useLocation()
  const state = location.state as LocationState
  return { location, ...state }
}

/**
 * React Router useNavigate hook that replaces `:regionId` in provided path with current regionId
 */
export const useNavigate = () => {
  const navigate = useNavigate_()
  const {
    data: { regionId },
  } = useCurrentRegion()
  return useCallback(
    (
      to: string,
      { searchParams, ...options }: NavigateOptionsWithParams = {} as NavigateOptionsWithParams
    ) => {
      const pathname = generatePathWithRegionId(to, regionId)
      const search = searchParams
        ? createSearchParams(searchParams as URLSearchParamsInit).toString()
        : undefined
      navigate(omitBy({ pathname, search }, isUndefined), options)
    },
    [navigate, regionId]
  )
}

/**
 * Navigate to the provided location when the regionId path param changes
 */
export const useNavigateOnRegionChange = ({ to, replace }: { to: string; replace?: boolean }) => {
  const navigate = useNavigate_()
  const { regionId } = useParams()
  const [firstRegionId] = useState(regionId)
  const regionIdHasChanged = regionId !== firstRegionId
  useEffect(() => {
    if (regionIdHasChanged) navigate(generatePathWithRegionId(to, regionId), { replace })
  }, [navigate, regionId, regionIdHasChanged, to, replace])
}
